import { useEffect, useMemo } from 'react';
import { ReactComponent as RepoIcon } from '@elementary/common-ui/assets/repo.svg';
import { Wizard } from '@elementary/common-ui/components';
import type { WizardStep } from '@elementary/common-ui/components/Wizard';
import {
  capitalize,
  pluralizeString,
} from '@elementary/common-ui/utils/common';
import { useMutation } from '@tanstack/react-query';
import { Controller, useForm, FormProvider } from 'react-hook-form';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import useSnackbar from 'shared/hooks/useSnackbar';
import useTicketField from 'shared/hooks/useTicketField';
import ConnectTicketDropdown from 'shared/components/ConnectTicketDropdown';
import { CreateTicketDialogs } from 'shared/components/CreateTicketDialogs';
import { deduplicateVersionedModels } from 'shared/utils/versionUtils';
import * as api from '../../services/api';
import { EditResourcesApi } from '../../services/api';
import useBulkEditAssets from './useBulkEditAssets';
import ReviewSubmitStep from './ReviewSubmitStep';
import SuggestedChangesStep from './SuggestedChangesStep';
import { BulkEditFormData, BulkEditProperty } from './types';
import {
  bulkEditPropertyTypeMap,
  getModifiedTableData,
  getArrayPropertyChanges,
} from './utils';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void;
  modelIds: string[];
  editProperty?: BulkEditProperty;
};

const BulkEditWizard = ({
  isOpen,
  onClose,
  onSubmit,
  modelIds = [],
  editProperty = 'tags',
}: Props) => {
  const { assets, isFetching: isAssetsFetching } = useBulkEditAssets(modelIds);
  const selectedEnvId = useSelectedEnv();
  const { openSnackbar } = useSnackbar();

  const methods = useForm<BulkEditFormData>({
    defaultValues: {
      initialTableData: [],
      tableData: [],
      ticketId: null,
    },
    mode: 'onChange',
  });

  const {
    ticketIdRef,
    requireTicketId,
    enabledIntegrations,
    activeIntegration,
    handleIntegrationSelect,
    createTicketDialogOpen,
    handleCreateTicketClick,
    handleCreateTicketClose,
    handleTicketCreated,
    resetTicket,
  } = useTicketField({
    envId: selectedEnvId,
    ticketId: methods.watch('ticketId') ?? null,
    setTicketId: (id) =>
      methods.setValue('ticketId', id, {
        shouldDirty: true,
        shouldValidate: true,
      }),
  });

  const initialTableData = useMemo(
    () =>
      // Deduplicate versioned models by model_name, keeping the latest version
      deduplicateVersionedModels(
        assets,
        (model) => `${model.model_name}|${model.source_name || ''}`,
      ),
    [assets],
  );

  const initializeForm = () => {
    methods.setValue('initialTableData', initialTableData);
    methods.setValue('tableData', initialTableData);
    methods.setValue('editProperty', editProperty);
    methods.setValue('editPropertyType', bulkEditPropertyTypeMap[editProperty]);
  };

  useEffect(() => {
    if (isOpen) {
      initializeForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialTableData]);

  const handleCloseWizard = () => {
    methods.reset();
    resetTicket();
    onClose();
  };

  const { mutateAsync: editResources, isLoading: editResourcesLoading } =
    useMutation((data: EditResourcesApi) =>
      api.editResources(data, selectedEnvId!, ticketIdRef.current || undefined),
    );

  const handleSubmit = async () => {
    const { tableData, editPropertyType } = methods.watch();

    const modifiedTableData = getModifiedTableData(
      initialTableData,
      tableData,
      editProperty,
      editPropertyType,
    );

    const submitVal = modifiedTableData.map((model) => {
      const initialModel = initialTableData.find(
        (initial) => initial.unique_id === model.unique_id,
      );

      if (editPropertyType === 'array') {
        const initialValues = (initialModel?.[editProperty] as string[]) || [];
        const currentValues = (model[editProperty] as string[]) || [];
        const changes = getArrayPropertyChanges(initialValues, currentValues);

        return {
          resource_name: model.model_name,
          source_name: model.source_name,
          asset_id: model.unique_id,
          [editProperty]: changes,
        };
      } else {
        return {
          resource_name: model.model_name,
          source_name: model.source_name,
          asset_id: model.unique_id,
          [editProperty]: !model[editProperty] ? null : model[editProperty],
        };
      }
    });

    try {
      await editResources({ data: { edit_resources: submitVal } });
      openSnackbar({
        message:
          'Creating a pull request for your changes. This may take a few seconds...',
        autoHide: false,
      });
    } finally {
      onSubmit();
    }
  };

  const validateSuggestedChangesStep = () => {
    const { tableData, isGenerateAllLoading } = methods.watch();
    if (isGenerateAllLoading) return false;
    return JSON.stringify(tableData) !== JSON.stringify(initialTableData);
  };

  const wizardSteps: WizardStep[] = [
    {
      title: 'Suggested Changes',
      description: pluralizeString(
        modelIds.length,
        'Row selected',
        'Rows selected',
      ),
      content: (
        <FormProvider {...methods}>
          <SuggestedChangesStep isLoading={isAssetsFetching} />
        </FormProvider>
      ),
      isValid: validateSuggestedChangesStep,
      tooltipMessage: 'Make changes to continue',
    },
    {
      title: 'Review & Submit',
      description: pluralizeString(
        modelIds.length,
        'Row selected',
        'Rows selected',
      ),
      content: (
        <FormProvider {...methods}>
          <ReviewSubmitStep />
        </FormProvider>
      ),
      footerRight: activeIntegration ? (
        <>
          <Controller
            name="ticketId"
            control={methods.control}
            render={({ field }) => (
              <ConnectTicketDropdown
                envId={selectedEnvId!}
                enabledIntegrations={enabledIntegrations}
                onSelect={(integration, ticket) => {
                  handleIntegrationSelect(integration.id);
                  field.onChange(ticket.key);
                }}
                onCreateClick={(integration) =>
                  handleCreateTicketClick(integration.id)
                }
                selectedTicket={
                  field.value
                    ? {
                        key: field.value,
                        integrationType: activeIntegration.type,
                      }
                    : null
                }
                onClearSelected={() => field.onChange(null)}
                popoverPlacement="above"
              />
            )}
          />
          <CreateTicketDialogs
            open={createTicketDialogOpen}
            onClose={handleCreateTicketClose}
            onTicketCreated={handleTicketCreated}
            activeIntegration={activeIntegration}
            envId={selectedEnvId!}
          />
        </>
      ) : undefined,
      isValid:
        requireTicketId && !!activeIntegration
          ? () => !!methods.watch('ticketId')
          : undefined,
      tooltipMessage:
        requireTicketId && !!activeIntegration
          ? 'Select a ticket to continue'
          : undefined,
    },
  ];

  return (
    <Wizard
      isOpen={isOpen}
      title={`Modify ${capitalize(editProperty)}`}
      steps={wizardSteps}
      onClose={handleCloseWizard}
      onSubmit={handleSubmit}
      isLoading={editResourcesLoading}
      submitButtonText="Submit & Open PR"
      submitButtonIcon={<RepoIcon />}
      submitButtonIconPos="start"
    />
  );
};

export default BulkEditWizard;
